import { Button } from "../auvelity-ui"
import FocusTrap from "focus-trap-react"
import { GatsbyImage } from "gatsby-plugin-image"
import React, { useEffect, useState, useContext, useRef } from "react"
import tw from "twin.macro"
import { useIsMount } from "../../utils"
import { myContext } from "../../context/Provider"
import { useQueryParam, NumberParam } from "use-query-params"

const Modal = ({
  modalType = "exit",
  modalName,
  variant = "blue",
  yesText = "Continue",
  yesLink,
  noText = "Cancel",
  isCloseButton = true,
  close,
  modalChildren,
  addClassForGTM,
  image,
  activateFocusTrap = true,
}) => {
  const context = useContext(myContext)
  const [modalVisible, setModalVisible] = useState(false)
  const [focusActive, setFocusActive] = useState(activateFocusTrap)
  const blueBg = tw`bg-blue text-white`
  const whiteBg = tw`bg-white text-black`
  const textAlign = modalType === "exit" ? tw`text-center` : tw`text-left`
  const background = variant === "white" ? whiteBg : blueBg
  const overlayColor = !modalVisible
    ? tw`bg-transparent`
    : variant === "white"
    ? tw`bg-black bg-opacity-75`
    : tw`bg-white bg-opacity-75`
  const closeColor = variant === "white" ? "#21174b" : "#ffffff"
  const yesLinkRef = useRef()

  const isMount = useIsMount()
  const [showModal] = useQueryParam("showModal", NumberParam)

  const handleExitClick = e => {
    e.preventDefault()
    const destination =
      modalType === "exit" && document
        ? document.getElementById("yesLink").href
        : yesLink
    window.open(destination, "new")
    close()
  }

  useEffect(() => {
    if (isMount) {
      setTimeout(() => {
        setModalVisible(true)
      }, 200)
    }
    if (window) {
      window.toggleFocusTrap = toggleFocusTrap
    }

    if (document) document.addEventListener("keyup", closeOnEscape)
    return () => document.removeEventListener("keyup", closeOnEscape)
  })

  const toggleFocusTrap = status => {
    setFocusActive(status)
  }

  const closeOnEscape = e => {
    if (e.key === "Escape") {
      close()
      if (modalType === "savings-card-modal")
        sessionStorage.setItem("savings-card-modal", "closed")
    }
  }


  return (
    <div
      className="overlay"
      css={[
        tw`w-full h-full py-8 flex justify-center items-center `,
        overlayColor,
        context.isMobile && `max-height: ${window.innerHeight}px`,
      ]}
    >
      <FocusTrap
        active={focusActive}
        focusTrapOptions={{
          allowOutsideClick(event) {
            return showModal === 0
          },
          returnFocusOnDeactivate: true,
        }}
      >
        <div
          className="modalBody"
          css={[
            tw`max-h-full overflow-auto rounded-tl-regular rounded-br-regular px-8 py-16 w-full mx-4 z-50 relative opacity-0 transform -translate-y-5 transition-all ease-in-out duration-500 md:(p-12 w-9/12) lg:(max-w-screen-md)`,
            background,
            textAlign,
            modalVisible && tw`opacity-100 translate-y-0`,
          ]}
        >
          {isCloseButton && (
            <button
              onClick={() => {
                setModalVisible(false)
                setTimeout(close, 500)
                if (modalType === "savings-card-modal")
                  sessionStorage.setItem("savings-card-modal", "closed")
              }}
              aria-label="close"
              tw="absolute top-5 right-5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 36 36"
              >
                <defs>
                  <clipPath id="r34xa">
                    <path
                      fill="#fff"
                      d="M18 36c9.941 0 18-8.059 18-18S27.941 0 18 0 0 8.059 0 18s8.059 18 18 18z"
                    />
                  </clipPath>
                </defs>
                <g>
                  <g
                    clipPath="url(#clip-CF3D34CE-9DFF-4B81-8DE1-4E40519EEFC8)"
                    transform="rotate(-315 18 18)"
                  >
                    <g>
                      <path
                        fill="none"
                        stroke={closeColor}
                        strokeLinecap="round"
                        strokeMiterlimit="20"
                        strokeWidth="2"
                        d="M18.5 10.5v15"
                      />
                    </g>
                    <g>
                      <path
                        fill="none"
                        stroke={closeColor}
                        strokeLinecap="round"
                        strokeMiterlimit="20"
                        strokeWidth="2"
                        d="M11 18h15"
                      />
                    </g>
                    <g>
                      <path
                        fill="none"
                        stroke={closeColor}
                        strokeMiterlimit="20"
                        strokeWidth="4"
                        d="M18 36c9.941 0 18-8.059 18-18S27.941 0 18 0 0 8.059 0 18s8.059 18 18 18z"
                        clipPath='url("#r34xa")'
                      />
                    </g>
                  </g>
                </g>
              </svg>
            </button>
          )}

          {modalType === "exit" ? (
            <>
              <div tw=" lg:p-10">
                <h2 tw="text-yellow text-2xl font-bold">
                  You are about to leave Auvelity.com
                </h2>
                <p tw="mt-6 px-3 text-center text-sm tracking-tight	md:(text-base mt-8) lg:(px-6)">
                  You will now be sent to a website that is not controlled or
                  owned by Axsome Therapeutics, Inc.
                </p>
                <p tw="mt-4 px-4 text-center text-sm tracking-tight	md:(text-base mt-5) lg:(px-6)">
                  Axsome Therapeutics, Inc. is not responsible or liable for the
                  website you are about to go to, which may not be appropriate
                  for everyone.
                </p>

                <div tw="flex justify-evenly mt-7 flex md:(flex-row justify-center mt-10)">
                  <div tw="mr-2 md:(mr-3)">
                    <Button
                      id="yesLink"
                      size="lg"
                      env="blue"
                      onClick={handleExitClick}
                      as="link"
                      href={yesLink}
                      data-external-url={yesLink}
                      addClassForGTM={`gtm-interstitial gtm-continue ${addClassForGTM}`}
                      data-gtm-interstitial-name={`entrance + ${modalName}`}
                      elementName={modalName}
                    >
                      {yesText}
                    </Button>
                  </div>
                  <div tw="md:ml-3">
                    <Button
                      size="lg"
                      variant="white"
                      env="blue"
                      onClick={close}
                      data-gtm-interstitial-name={`exit + ${modalName}`}
                      addClassForGTM="gtm-interstitial gtm-close"
                    >
                      {noText}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
          {modalType === "footnotes" && !image ? (
            <>
              <h3 tw="text-blue mb-3 font-bold md:text-2xl">
                Footnotes and Definitions
              </h3>
            </>
          ) : null}
          {modalType === "tooltip" ? (
            <div dangerouslySetInnerHTML={{ __html: modalChildren }} />
          ) : (
            modalChildren && modalChildren
          )}
          {image && <GatsbyImage image={image} />}
        </div>
      </FocusTrap>
    </div>
  )
}

export default Modal
